import React, { createContext, useContext, useState, useEffect } from 'react';

// Translation files
const translations = {
  en_US: {
    // Phone Search Page
    'phoneSearch.title': 'Search Conversations by Phone Number',
    'phoneSearch.phoneNumber': 'Phone Number',
    'phoneSearch.phoneNumberPlaceholder': 'Enter phone number (e.g., +85212345678 or 85212345678)',
    'phoneSearch.phoneNumberHint': 'Enter phone number with or without country code. Partial matches are supported.',
    'phoneSearch.search': 'Search',
    'phoneSearch.searching': 'Searching...',
    'phoneSearch.loading': 'Loading conversations...',
    'phoneSearch.error': 'Failed to load conversations. Please try again later.',
    'phoneSearch.errorRequired': 'Please enter a phone number',
    'phoneSearch.found': 'Found',
    'phoneSearch.conversations': 'conversations',
    'phoneSearch.conversation': 'conversation',
    'phoneSearch.onThisPage': 'on this page',
    'phoneSearch.page': 'Page',
    'phoneSearch.of': 'of',
    'phoneSearch.phone': 'Phone',
    'phoneSearch.lastMessage': 'Last message',
    'phoneSearch.noResults': 'No conversations found for this phone number.',
    'phoneSearch.noResultsHint': 'Try searching with a different format or partial number.',
    'phoneSearch.enterPhone': 'Enter a phone number above to search for conversations.',
    'phoneSearch.unknown': 'Unknown',
    'phoneSearch.navLabel': 'Phone Search',
  },
  zh_HK: {
    // Phone Search Page
    'phoneSearch.title': '按電話號碼搜尋對話',
    'phoneSearch.phoneNumber': '電話號碼',
    'phoneSearch.phoneNumberPlaceholder': '輸入電話號碼（例如：+85212345678 或 85212345678）',
    'phoneSearch.phoneNumberHint': '可輸入帶或不帶國家代碼的電話號碼。支援部分匹配。',
    'phoneSearch.search': '搜尋',
    'phoneSearch.searching': '搜尋中...',
    'phoneSearch.loading': '載入對話中...',
    'phoneSearch.error': '載入對話失敗。請稍後再試。',
    'phoneSearch.errorRequired': '請輸入電話號碼',
    'phoneSearch.found': '找到',
    'phoneSearch.conversations': '個對話',
    'phoneSearch.conversation': '個對話',
    'phoneSearch.onThisPage': '在此頁面',
    'phoneSearch.page': '第',
    'phoneSearch.of': '頁，共',
    'phoneSearch.phone': '電話',
    'phoneSearch.lastMessage': '最後訊息',
    'phoneSearch.noResults': '找不到此電話號碼的對話。',
    'phoneSearch.noResultsHint': '請嘗試使用不同的格式或部分號碼進行搜尋。',
    'phoneSearch.enterPhone': '請在上方輸入電話號碼以搜尋對話。',
    'phoneSearch.unknown': '未知',
    'phoneSearch.navLabel': '電話搜尋',
  },
  zh_CN: {
    // Phone Search Page
    'phoneSearch.title': '按电话号码搜索对话',
    'phoneSearch.phoneNumber': '电话号码',
    'phoneSearch.phoneNumberPlaceholder': '输入电话号码（例如：+85212345678 或 85212345678）',
    'phoneSearch.phoneNumberHint': '可输入带或不带国家代码的电话号码。支持部分匹配。',
    'phoneSearch.search': '搜索',
    'phoneSearch.searching': '搜索中...',
    'phoneSearch.loading': '加载对话中...',
    'phoneSearch.error': '加载对话失败。请稍后再试。',
    'phoneSearch.errorRequired': '请输入电话号码',
    'phoneSearch.found': '找到',
    'phoneSearch.conversations': '个对话',
    'phoneSearch.conversation': '个对话',
    'phoneSearch.onThisPage': '在此页面',
    'phoneSearch.page': '第',
    'phoneSearch.of': '页，共',
    'phoneSearch.phone': '电话',
    'phoneSearch.lastMessage': '最后消息',
    'phoneSearch.noResults': '找不到此电话号码的对话。',
    'phoneSearch.noResultsHint': '请尝试使用不同的格式或部分号码进行搜索。',
    'phoneSearch.enterPhone': '请在上方输入电话号码以搜索对话。',
    'phoneSearch.unknown': '未知',
    'phoneSearch.navLabel': '电话搜索',
  },
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to en_US
    return localStorage.getItem('dashboard_language') || 'en_US';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('dashboard_language', language);
  }, [language]);

  const t = (key, params = {}) => {
    let translation = translations[language]?.[key] || translations['en_US'][key] || key;

    // Simple parameter replacement
    Object.keys(params).forEach(param => {
      translation = translation.replace(`{${param}}`, params[param]);
    });

    return translation;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  return (
    <I18nContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback to English if context is not available
    const fallbackT = (key) => translations['en_US'][key] || key;
    return { language: 'en_US', t: fallbackT, changeLanguage: () => {} };
  }
  return context;
};
